/**
 * 合集: one collection — its illustration, name and description, then
 * every case in it in the authored order.
 */

import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { CoachingNavParams } from '@euler/app/flows/coaching';
import { EmptyView } from '@euler/components/EmptyView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useMemo } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { CaseCard } from '../../components/CaseCard';
import { CaseCardSkeleton } from '../../components/CaseCardSkeleton';
import { CaseTags } from '../../components/CaseTags';
import { CollectionIllustration } from '../../components/CollectionIllustration';
import { useCoachingCatalogueState } from '../../functions/useCoachingCatalogue';
import { useCollectionCases } from '../../functions/useCollectionCases';

export const CoachingCollectionScreen = wrapNavigatorScreen(
  ({ slug }: { slug: string }) => {
    const styles = useStyles();
    const { rpx } = useResponsiveContext();
    const wide = useIsDesktopLayout();
    const navigation = useNavigation<StackNavigationProp<CoachingNavParams>>();
    const catalogue = useCoachingCatalogueState();
    const collection =
      catalogue.state.kind === 'ready'
        ? catalogue.state.catalogue.collections.find(item => item.slug === slug)
        : undefined;
    const { state, reload } = useCollectionCases(slug);
    if (catalogue.state.kind === 'failed')
      return (
        <EmptyView message="合集暂时没加载出来" onRetry={catalogue.reload} />
      );
    if (catalogue.state.kind === 'ready' && !collection)
      return <EmptyView message="没有找到这个合集" />;
    return (
      <ScrollView contentContainerStyle={styles.page}>
        <View style={styles.head}>
          <CollectionIllustration slug={slug} size={rpx(72)} />
          <View style={styles.headText}>
            <Typography.Text size={rpx(20)} bold color={aiColors.foreground}>
              {collection?.name ?? ' '}
            </Typography.Text>
            <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
              {collection ? `${collection.caseCount} 个场景` : ' '}
            </Typography.Text>
          </View>
        </View>
        {collection?.description ? (
          <Typography.Text
            size={rpx(13)}
            color={aiColors.mutedForeground}
            lineHeight={rpx(20)}
            style={styles.description}
          >
            {collection.description}
          </Typography.Text>
        ) : null}
        {state.kind === 'failed' ? (
          <EmptyView
            message="案例暂时没加载出来"
            onRetry={reload}
            noFullHeight
          />
        ) : state.kind === 'ready' && !state.cases.length ? (
          <EmptyView message="这个合集还没有案例" noFullHeight />
        ) : (
          <View style={[styles.grid, wide && styles.gridWide]}>
            {state.kind === 'loading'
              ? [0, 1, 2].map(index => (
                  <View key={index} style={wide ? styles.cellWide : null}>
                    <CaseCardSkeleton />
                  </View>
                ))
              : state.cases.map(card => (
                  <View key={card.id} style={wide ? styles.cellWide : null}>
                    <CaseCard
                      card={card}
                      onPress={() =>
                        navigation.push('case', { caseId: card.id })
                      }
                      footer={<CaseTags tags={card.tags} max={3} />}
                    />
                  </View>
                ))}
          </View>
        )}
      </ScrollView>
    );
  },
  { title: '合集' },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        page: {
          width: '100%',
          maxWidth: rpx(768),
          alignSelf: 'center',
          padding: rpx(16),
          paddingBottom: rpx(40),
        },
        head: { flexDirection: 'row', alignItems: 'center', gap: rpx(14) },
        headText: { flex: 1, gap: rpx(4) },
        description: { marginTop: rpx(12) },
        grid: { marginTop: rpx(20), gap: rpx(10) },
        gridWide: { flexDirection: 'row', flexWrap: 'wrap' },
        cellWide: { width: '49%' },
      }),
    [rpx],
  );
}
